import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BarChart3,
  Calendar,
  Clock,
  Globe,
  Heart,
  Lock,
  MessageSquare,
  Monitor,
  Phone,
  Search,
  Settings,
  Shield,
  Sparkles,
  Star,
  Users,
  Zap,
  type LucideIcon,
} from 'lucide-react';

import { startPageImageUrl } from '../lib/api';
import { toAppLauncherLanguage } from '../lib/appLauncherHandoff';
import {
  BUILT_IN_FEATURES,
  BUILT_IN_TEXT_KEYS,
  appLauncherEmbedFrameUrl,
  appLauncherEmbedOrigin,
  blockAnchorId,
  pickText,
} from './content';
import { RichText } from './RichText';
import type { LocalizedText, StartPageBlock } from './types';

/** The icons a feature card can pick, by the name stored on the item. */
export const FEATURE_ICONS: Record<string, LucideIcon> = {
  zap: Zap,
  users: Users,
  monitor: Monitor,
  phone: Phone,
  shield: Shield,
  globe: Globe,
  sparkles: Sparkles,
  clock: Clock,
  star: Star,
  heart: Heart,
  lock: Lock,
  chart: BarChart3,
  message: MessageSquare,
  calendar: Calendar,
  search: Search,
  settings: Settings,
};

interface StartPageBlocksProps {
  blocks: StartPageBlock[];
  onSignIn: () => void;
  isLoading: boolean;
  /** The AppLauncher frame origin; defaults to this build's. Injectable for tests and the editor. */
  embedOrigin?: string | null;
}

/** Renders the visible start-page blocks in order. Shared by the landing page and the portal preview. */
export function StartPageBlocks({ blocks, onSignIn, isLoading, embedOrigin }: StartPageBlocksProps) {
  const origin = embedOrigin === undefined ? appLauncherEmbedOrigin() : embedOrigin;

  return (
    <>
      {blocks.filter((block) => !block.hidden).map((block) => (
        <StartPageBlockView key={block.id} block={block} onSignIn={onSignIn} isLoading={isLoading} embedOrigin={origin} />
      ))}
    </>
  );
}

interface BlockViewProps {
  block: StartPageBlock;
  onSignIn: () => void;
  isLoading: boolean;
  embedOrigin: string | null;
}

function StartPageBlockView({ block, onSignIn, isLoading, embedOrigin }: BlockViewProps) {
  const { t, i18n } = useTranslation();
  const language = i18n.language;

  /** Authored text, else the built-in string for this block type's field, else null. */
  const text = (field: keyof typeof BUILT_IN_TEXT_KEYS.hero | 'eyebrow', value: LocalizedText | null | undefined) => {
    const authored = pickText(value, language);
    if (authored) return authored;
    const keys = BUILT_IN_TEXT_KEYS[block.type as keyof typeof BUILT_IN_TEXT_KEYS] as Partial<Record<string, string>> | undefined;
    const key = keys?.[field];
    return key ? t(key) : null;
  };

  const signInButton = (label: string | null) => (
    <button className="landing-btn landing-btn-primary landing-btn-lg" onClick={onSignIn} disabled={isLoading}>
      {isLoading ? t('startPage.signingIn') : label}
    </button>
  );

  const image = resolveImage(block, language);
  const id = blockAnchorId(block);

  switch (block.type) {
    case 'hero': {
      const eyebrow = text('eyebrow', block.eyebrow);
      const body = text('body', block.body);
      return (
        <section id={id} className={`landing-hero${image ? ' landing-hero-with-image' : ''}`}>
          <div className="landing-hero-inner">
            <div className="landing-hero-content">
              {eyebrow && <span className="landing-eyebrow landing-eyebrow-badge">{eyebrow}</span>}
              <h1 className="landing-hero-title">
                <span className="landing-hero-gradient-text">{text('title', block.title)}</span>
              </h1>
              {text('caption', block.caption) && <p className="landing-hero-subtitle">{text('caption', block.caption)}</p>}
              {body && <RichText className="landing-hero-desc landing-rich-text" text={body} />}
              <div className="landing-hero-actions">{signInButton(text('buttonLabel', block.buttonLabel))}</div>
            </div>
            {image && (
              <div className="landing-hero-media">
                <img src={image.src} alt={image.alt} className="landing-media-img" />
              </div>
            )}
          </div>
        </section>
      );
    }

    case 'text': {
      const body = pickText(block.body, language);
      return (
        <section id={id} className="landing-block">
          <div className="landing-block-inner landing-block-narrow">
            <SectionHeading eyebrow={pickText(block.eyebrow, language)} title={pickText(block.title, language)} />
            {body && <RichText className="landing-rich-text" text={body} />}
          </div>
        </section>
      );
    }

    case 'image': {
      if (!image) return null;
      const caption = pickText(block.caption, language);
      return (
        <section id={id} className="landing-block">
          <figure className="landing-block-inner landing-figure">
            <img src={image.src} alt={image.alt} className="landing-media-img" loading="lazy" />
            {caption && <figcaption>{caption}</figcaption>}
          </figure>
        </section>
      );
    }

    case 'textImage': {
      const body = pickText(block.body, language);
      return (
        <section id={id} className="landing-block">
          <div className={`landing-block-inner landing-split${block.imagePosition === 'left' ? ' landing-split-image-left' : ''}`}>
            <div className="landing-split-text">
              <SectionHeading eyebrow={pickText(block.eyebrow, language)} title={pickText(block.title, language)} align="start" />
              {body && <RichText className="landing-rich-text" text={body} />}
            </div>
            {image && (
              <div className="landing-split-media">
                <img src={image.src} alt={image.alt} className="landing-media-img" loading="lazy" />
              </div>
            )}
          </div>
        </section>
      );
    }

    case 'features': {
      const body = pickText(block.body, language);
      const items = block.items?.length
        ? block.items.map((item) => ({
            icon: item.icon ?? null,
            title: pickText(item.title, language),
            body: pickText(item.body, language),
          }))
        : BUILT_IN_FEATURES.map((item) => ({ icon: item.icon, title: t(item.title), body: t(item.body) }));

      return (
        <section id={id} className="landing-features">
          <div className="landing-features-inner">
            <SectionHeading eyebrow={pickText(block.eyebrow, language)} title={text('title', block.title)} />
            {body && <RichText className="landing-rich-text landing-section-intro" text={body} />}
            <div className="landing-features-grid">
              {items.map((item, index) => {
                const Icon = item.icon && Object.prototype.hasOwnProperty.call(FEATURE_ICONS, item.icon)
                  ? FEATURE_ICONS[item.icon]!
                  : Sparkles;
                return (
                  <div key={index} className="landing-feature-card">
                    <div className="landing-feature-icon">
                      <Icon size={32} aria-hidden="true" />
                    </div>
                    {item.title && <h3 className="landing-feature-title">{item.title}</h3>}
                    {item.body && <RichText className="landing-feature-desc landing-rich-text" text={item.body} />}
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      );
    }

    case 'signIn': {
      const body = text('body', block.body);
      return (
        <section id={id} className="landing-cta">
          <div className="landing-cta-inner">
            <h2 className="landing-cta-title">{text('title', block.title)}</h2>
            {body && <RichText className="landing-cta-subtitle landing-rich-text" text={body} />}
            {signInButton(text('buttonLabel', block.buttonLabel))}
          </div>
        </section>
      );
    }

    case 'appLauncherEmbed': {
      if (!embedOrigin || !block.appCode || !block.embedId) return null;
      const title = pickText(block.title, language);
      return (
        <section id={id} className="landing-block">
          <div className="landing-block-inner">
            {title && <SectionHeading eyebrow={null} title={title} />}
            <AppLauncherEmbedFrame
              origin={embedOrigin}
              src={appLauncherEmbedFrameUrl(
                embedOrigin,
                { appCode: block.appCode, embedId: block.embedId },
                toAppLauncherLanguage(language),
              )}
              embedId={block.embedId}
              title={title ?? 'Exobe'}
            />
          </div>
        </section>
      );
    }

    default:
      return null;
  }
}

function SectionHeading({ eyebrow, title, align = 'center' }: { eyebrow: string | null; title: string | null; align?: 'center' | 'start' }) {
  if (!eyebrow && !title) return null;
  return (
    <div className={`landing-section-heading${align === 'start' ? ' landing-section-heading-start' : ''}`}>
      {eyebrow && <span className="landing-eyebrow">{eyebrow}</span>}
      {title && <h2 className="landing-section-title">{title}</h2>}
    </div>
  );
}

function resolveImage(block: StartPageBlock, language: string): { src: string; alt: string } | null {
  const src = block.imageId ? startPageImageUrl(block.imageId) : block.imageUrl;
  return src ? { src, alt: pickText(block.imageAlt, language) ?? '' } : null;
}

/** Matches `HEIGHT_MESSAGE` in AppLauncher's `embed.js`. */
const EMBED_HEIGHT_MESSAGE = 'exobe:embed:height';

/**
 * AppLauncher's app-information frame, with the same sandbox and auto-height protocol as its `embed.js`
 * loader. The loader itself cannot run here: the CSP allows no third-party script, which is the point.
 * The embed must list this site's origin as allowed in AppLauncher, or the frame refuses to render.
 */
function AppLauncherEmbedFrame({ origin, src, embedId, title }: { origin: string; src: string; embedId: string; title: string }) {
  const frameRef = useRef<HTMLIFrameElement>(null);
  const [height, setHeight] = useState(320);

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      // The source check stops another frame on the page, e.g. a second embed, from resizing this one. The
      // frame is sandboxed without allow-same-origin, so its messages arrive from the opaque origin "null".
      if (event.source !== frameRef.current?.contentWindow) return;
      if (event.origin !== 'null' && event.origin !== origin) return;
      const data = event.data as { type?: unknown; embedId?: unknown; height?: unknown } | null;
      if (!data || data.type !== EMBED_HEIGHT_MESSAGE || data.embedId !== embedId) return;
      const next = Number(data.height);
      if (Number.isFinite(next) && next > 0) setHeight(Math.ceil(next));
    };

    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [origin, embedId]);

  return (
    <iframe
      ref={frameRef}
      src={src}
      title={title}
      loading="lazy"
      scrolling="no"
      // No allow-same-origin, exactly as embed.js: the frame needs no storage and cannot reach its origin's.
      sandbox="allow-scripts allow-popups allow-popups-to-escape-sandbox allow-top-navigation-by-user-activation"
      className="landing-embed-frame"
      style={{ height: `${height}px` }}
    />
  );
}
